import { FadeUp } from "@/components/Reveal";
import { ADDRESS, PHONE_DISPLAY } from "@/data/site";

const LegalShell = ({ eyebrow, title, updated, children, testid }) => (
  <main className="pt-[72px]" data-testid={testid}>
    <section className="border-b border-white/10 bg-[#0C0C0F]">
      <div className="mx-auto max-w-4xl px-4 py-20 sm:px-8 lg:py-24">
        <FadeUp>
          <p className="mono text-xs uppercase tracking-[0.2em] text-[#E9C766]">{eyebrow}</p>
          <h1 className="font-display mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            {title}
          </h1>
          <p className="mt-4 text-sm text-[#6B7280]">Last updated: {updated}</p>
        </FadeUp>
      </div>
    </section>
    <section className="mx-auto max-w-4xl px-4 py-16 sm:px-8">
      <FadeUp className="space-y-10">{children}</FadeUp>
    </section>
  </main>
);

const Block = ({ title, children }) => (
  <div>
    <h2 className="font-display text-xl font-bold text-white">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-[#A1A1AA] sm:text-base">
      {children}
    </div>
  </div>
);

export const PrivacyPolicy = () => (
  <LegalShell eyebrow="Legal" title="Privacy Policy" updated="July 2026" testid="privacy-policy-page">
    <Block title="1. What we collect">
      <p>
        When you submit an inquiry or contact form on this website, we collect the information you
        provide: your name, email address, phone number, company name, and the contents of your
        message. We also collect standard technical data such as browser type and pages visited
        for analytics purposes.
      </p>
    </Block>
    <Block title="2. How we use it">
      <p>
        We use your information solely to respond to your inquiry, prepare quotes, deliver our
        services, and — only if you ask — send occasional updates. We do not sell, rent, or share
        your personal information with third parties for their marketing purposes.
      </p>
    </Block>
    <Block title="3. Cookies & analytics">
      <p>
        This site may use cookies and privacy-conscious analytics to understand how visitors use
        our pages and to improve performance. You can disable cookies in your browser at any time.
      </p>
    </Block>
    <Block title="4. Data retention & security">
      <p>
        Inquiry submissions are stored securely and retained only as long as needed to handle your
        request and any resulting business relationship. We apply reasonable technical and
        organizational measures to protect your data.
      </p>
    </Block>
    <Block title="5. Your rights">
      <p>
        You may request access to, correction of, or deletion of your personal data at any time by
        contacting us at {PHONE_DISPLAY} or via WhatsApp. Spark Plug is located at {ADDRESS}.
      </p>
    </Block>
    <p className="rounded-xl border border-white/10 bg-[#131316] p-5 text-xs text-[#6B7280]">
      This policy is a working placeholder pending final legal review. Contact us with any
      questions before relying on it.
    </p>
  </LegalShell>
);

export const TermsConditions = () => (
  <LegalShell eyebrow="Legal" title="Terms & Conditions" updated="July 2026" testid="terms-page">
    <Block title="1. Services">
      <p>
        Spark Plug provides digital marketing, website development, app development, domain
        registration assistance, and shared hosting services. Specific deliverables, timelines and
        pricing are defined in individual quotes or agreements before work begins.
      </p>
    </Block>
    <Block title="2. Inquiries & quotes">
      <p>
        Submitting a form on this website creates an inquiry only — it does not create a contract,
        reserve a domain, or provision hosting. Domain availability and all-in-one package pricing
        are confirmed manually by our team.
      </p>
    </Block>
    <Block title="3. Hosting service levels">
      <p>
        Shared hosting plans target 99.9% uptime measured monthly, excluding scheduled maintenance
        announced in advance. Daily backups are provided as a courtesy; clients remain responsible
        for maintaining their own copies of critical data.
      </p>
    </Block>
    <Block title="4. Client responsibilities">
      <p>
        Clients are responsible for providing accurate content, timely feedback, and lawful
        materials. Spark Plug is not liable for losses arising from content supplied by the client
        or third-party platform changes (e.g., ad network or app store policy updates).
      </p>
    </Block>
    <Block title="5. Intellectual property">
      <p>
        Upon full payment, clients own the final delivered website or application. Spark Plug
        retains the right to showcase completed work in its portfolio unless otherwise agreed in
        writing.
      </p>
    </Block>
    <Block title="6. Contact">
      <p>
        Questions about these terms: {PHONE_DISPLAY} — {ADDRESS}.
      </p>
    </Block>
    <p className="rounded-xl border border-white/10 bg-[#131316] p-5 text-xs text-[#6B7280]">
      These terms are a working placeholder pending final legal review.
    </p>
  </LegalShell>
);

export const RefundPolicy = () => (
  <LegalShell eyebrow="Legal" title="Refund Policy" updated="July 2026" testid="refund-policy-page">
    <Block title="1. Project deposits">
      <p>
        Website, app, and marketing projects typically begin with a deposit. Deposits are
        refundable within 7 days of payment if no work has started. Once discovery, design, or
        development has begun, deposits become non-refundable.
      </p>
    </Block>
    <Block title="2. Hosting plans">
      <p>
        Shared hosting plans (Starter and Expert) may be cancelled within the first 30 days for a
        full refund of the annual fee. After 30 days, hosting fees are non-refundable but the
        service remains active until the end of the paid term.
      </p>
    </Block>
    <Block title="3. Domain registrations">
      <p>
        Domain registrations are processed with third-party registries and are non-refundable once
        completed, as registry fees cannot be recovered. If a requested domain is unavailable, no
        charge is made.
      </p>
    </Block>
    <Block title="4. How to request a refund">
      <p>
        Contact us at {PHONE_DISPLAY} or via WhatsApp with your name and project or invoice
        reference. Approved refunds are processed to the original payment method within 10
        business days.
      </p>
    </Block>
    <p className="rounded-xl border border-white/10 bg-[#131316] p-5 text-xs text-[#6B7280]">
      This policy is a working placeholder pending final legal review.
    </p>
  </LegalShell>
);
